<template>
    <EChart
        :options="chartOptions"
        card
        :header="$t('averageAvailabilityOfProductsByStores')"
        overlay
        :loading="request.getLoading() || requestCompare.getLoading()"
        :error="request.getError()"
        :tooltip="$t('tooltip')"
    >
        <template #nav>
            <div class="d-flex gap-3">
                <DropdownFilter v-model="currentSortBy" :items="['averageDesc', 'averageAsc', 'changeDesc', 'changeAsc']" :prefix="`${$t('sortBy')}: `" :disabled="loading" />
            </div>
        </template>
    </EChart>
</template>



<script>
    import camelCase from 'camelcase';
    import { calcPercentDiff } from '@/utils/number';
    import EChart from '@/components/common/EChart.vue';
    import DropdownFilter from '@/components/common/DropdownFilter.vue';

    export default {
        name: 'AverageAvailabilityOfProductsByStores',
        components: {
            EChart,
            DropdownFilter,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                request: this.parentComponent.requestData,
                requestCompare: this.parentComponent.requestDataCompare,
                currentSortBy: 'averageDesc',
            };
        },
        computed: {
            loading() {
                return this.request.getLoading();
            },
            data() {
                return this.request.getData() || {};
            },
            dataCompare() {
                return this.requestCompare.getData() || {};
            },
            chartData() {
                return this.computeChartData(this.data);
            },
            chartDataCompare() {
                return this.computeChartData(this.dataCompare);
            },
            sortedChartData() {
                const compareDict = {};
                this.chartDataCompare.forEach(item => {
                    compareDict[item.campaign] = item.avgAvailability;
                });
                const dataWithDiff = this.chartData.map(item => {
                    const compareValue = compareDict[item.campaign] || 0;
                    const diff = calcPercentDiff(item.avgAvailability, compareValue);
                    return { ...item, diff };
                });
                let sorted;
                switch (this.currentSortBy) {
                    case 'averageDesc':
                        sorted = [...dataWithDiff].sort((a, b) => b.avgAvailability - a.avgAvailability);
                        break;
                    case 'averageAsc':
                        sorted = [...dataWithDiff].sort((a, b) => a.avgAvailability - b.avgAvailability);
                        break;
                    case 'changeDesc':
                        sorted = [...dataWithDiff].sort((a, b) => b.diff - a.diff);
                        break;
                    case 'changeAsc':
                        sorted = [...dataWithDiff].sort((a, b) => a.diff - b.diff);
                        break;
                    default:
                        sorted = dataWithDiff;
                }
                return sorted;
            },
            chartOptions() {
                // jeśli brak danych do wyświetlenia, zwracamy pustą serię
                if (!this.sortedChartData.length) {
                    return { series: [] };
                }

                // przygotowujemy tablice: nazwy kampanii, wartości i procentowe zmiany
                const names = this.sortedChartData.map(item => item.campaign);
                const values = this.sortedChartData.map(item => item.avgAvailability);
                const diffs = this.sortedChartData.map(item => item.diff);
                const summary = values.reduce((sum, v) => sum + v, 0);

                return {
                    tooltip: {
                        trigger: 'axis',
                        axisPointer: { type: 'shadow' },
                    },
                    xAxis: {
                        type: 'value',
                        max: 'dataMax',
                    },
                    yAxis: [
                        // oś po lewej – kampanie
                        {
                            type: 'category',
                            inverse: true,
                            data: names,
                        },
                        // oś po prawej – procentowa zmiana
                        {
                            type: 'category',
                            inverse: true,
                            data: names,
                            position: 'right',
                            axisLine: { show: false },
                            axisTick: { show: false },
                            axisLabel: {
                                rich: {
                                    pos: {
                                        color: 'green',
                                        backgroundColor: 'rgba(0,255,0,0.1)',
                                        padding: [2, 4],
                                        borderRadius: 3,
                                    },
                                    zero: {
                                        color: '#666',
                                        backgroundColor: 'rgba(0,0,0,0.1)',
                                        padding: [2, 4],
                                        borderRadius: 3,
                                    },
                                    neg: {
                                        color: 'red',
                                        backgroundColor: 'rgba(255,0,0,0.1)',
                                        padding: [2, 4],
                                        borderRadius: 3,
                                    },
                                },
                                formatter: (value, idx) => {
                                    const diff = diffs[idx];
                                    const pct = this.$format.percent(diff);
                                    if (diff > 0) return `{pos|▲${pct}}`;
                                    if (diff < 0) return `{neg|▼${pct}}`;
                                    return `{zero|${pct}}`;
                                },
                            },
                        },
                    ],
                    series: [
                        {
                            type: 'bar',
                            stack: 'total',
                            // etykiety wewnątrz słupków: % udziału + liczba
                            label: {
                                show: true,
                                position: 'insideLeft',
                                formatter: params => {
                                    const value = params.data.value;
                                    return `${this.$format.percent((value * 100) / summary, 2)} (${value})`;
                                },
                            },
                            data: values.map(value => ({ value })),
                        },
                    ],
                };
            },
        },
        methods: {
            pascalCase(text) {
                return camelCase(text, { pascalCase: true });
            },
            computeChartData(source) {
                /* eslint-disable no-restricted-syntax, guard-for-in */
                const eventsByCampaign = {};
                Object.values(source).forEach(productObj => {
                    const charts = productObj.charts;
                    Object.keys(charts).forEach(chartKey => {
                        charts[chartKey].forEach(chartObj => {
                            const offer = chartObj.offer;
                            const campaign = offer.campaign;
                            const segments = chartObj.chart.segments;
                            if (segments && segments.length) {
                                segments.forEach(segment => {
                                    if (!eventsByCampaign[campaign]) {
                                        eventsByCampaign[campaign] = [];
                                    }
                                    eventsByCampaign[campaign].push({ time: segment.start, delta: +1 });
                                    eventsByCampaign[campaign].push({ time: segment.end, delta: -1 });
                                });
                            }
                        });
                    });
                });
                const campaignSummaries = [];
                for (const campaign in eventsByCampaign) {
                    const events = eventsByCampaign[campaign];
                    // Grupujemy zdarzenia wg tego samego timestampu
                    const grouped = {};
                    events.forEach(e => {
                        grouped[e.time] = (grouped[e.time] || 0) + e.delta;
                    });
                    const times = Object.keys(grouped)
                        .map(t => Number(t))
                        .sort((a, b) => a - b);
                    if (times.length === 0) continue; // eslint-disable-line no-continue
                    let cumulative = 0;
                    let weightedSum = 0;
                    let totalDuration = 0;
                    let prevTime = times[0];
                    times.forEach(time => {
                        const duration = time - prevTime;
                        weightedSum += cumulative * duration;
                        totalDuration += duration;
                        cumulative += grouped[time];
                        prevTime = time;
                    });
                    const avg = totalDuration ? weightedSum / totalDuration : cumulative;
                    campaignSummaries.push({
                        campaign,
                        avgAvailability: Math.round(avg),
                    });
                }
                // Sortujemy malejąco wg średniej dostępności
                campaignSummaries.sort((a, b) => b.avgAvailability - a.avgAvailability);
                return campaignSummaries;
                /* eslint-enable no-restricted-syntax, guard-for-in */
            },
        },
    };
</script>


<i18n lang="json" locale="pl" scope="local">
{
    "tooltip": "Wykres prezentuje ranking według średniej dostępności ofert produktów producenta per sklep [% oraz ilościowo] w sklepach wyświetlanych w widgecie (ustawienia domyślnie). Aby sprawdzić liczbę ofert w pozostałych sklepach, użyj filtra Sklep."
}
</i18n>

<i18n lang="json" locale="en" scope="local">
{
    "tooltip": "The chart presents the ranking according to the average availability of offers from the manufacturer's products per store [% and quantity] in stores displayed in the widget (settings by default). To check the number of offers in other stores, use the shop filter."
}
</i18n>
